import { useCallback, useMemo, useRef, useState } from "react";
import {
  To,
  NavigateOptions,
  useNavigate as useNavigateOriginal,
} from "react-router-dom";

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { isEqual, isString } from "lodash-es";

import { ApiInterceptorError } from "@classes/ApiInterceptorError";

import useAuth from "@hooks/useAuth";
import useMessage from "@hooks/useMessage";

export type ICustomApiOptions = {
  errorMessages?: { [status: number]: string };
  defaultErrorMessage?: string;
};

export type ICustomRequestConfig<D> = AxiosRequestConfig<D> & {
  /**
   * @param errorMessage The error message
   * @returns {boolean} True if the error snackbar should not be rendered
   */
  shouldSkipErrorSnackbar?: (errorMessage: string) => boolean;
};

export type IUseApi<T, D, E> = [
  (config?: ICustomRequestConfig<D>) => Promise<AxiosResponse<T, unknown>>,
  {
    data: T | null;
    error: E | null;
    loading: boolean;
    isFetched: boolean;
    reset: () => void;
    cancel: () => void;
  },
];

let retryCount = 0;
const MAX_RETRIES = 5;

export function useApi<T, D = unknown, E = unknown>(
  apiInstance: AxiosInstance,
  baseConfig: AxiosRequestConfig<D>,
  customApiOptions: ICustomApiOptions = {},
): IUseApi<T, D, E> {
  const message = useMessage();
  const { refreshAccessToken, ensureAccessToken, onExpireSession, user } =
    useAuth();
  const isLoggedIn = !!user;

  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<E | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [isFetched, setIsFetched] = useState<boolean>(false);

  const navigateRef = useRef(useNavigateOriginal());
  const baseConfigRef = useRef(baseConfig);
  const customApiOptionsRef = useRef(customApiOptions);
  const controllerRef = useRef<AbortController | null>(null);
  const requestConfigRef = useRef<AxiosRequestConfig<D> | undefined>(undefined);

  const baseConfigMemoized = useMemo(() => {
    if (!isEqual(baseConfig, baseConfigRef.current)) {
      baseConfigRef.current = baseConfig;
    }

    return baseConfigRef.current;
  }, [baseConfig]);

  const customApiOptionsMemoized = useMemo(() => {
    if (!isEqual(customApiOptions, customApiOptionsRef.current)) {
      customApiOptionsRef.current = customApiOptions;
    }

    return customApiOptionsRef.current;
  }, [customApiOptions]);

  const navigate = useCallback((to: To, options?: NavigateOptions) => {
    if (navigateRef.current) {
      navigateRef.current(to, options);
    }
  }, []);

  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setLoading(false);
    setIsFetched(false);
  }, []);

  const cancel = useCallback(() => {
    if (!controllerRef.current) return;
    reset();
    controllerRef.current.abort();
  }, [reset]);

  const handleError = useCallback(
    async (e: unknown, retrying = false) => {
      if (axios.isCancel(e)) return { handled: true };

      if (axios.isAxiosError(e)) {
        const data = e.response?.data as Record<
          string,
          | string
          | { errors: { errorMessage: string }[] }
          | { Erro: string }[]
          | { [key: string]: { [key: string]: string } }
        >;
        const apiMessage =
          data?.retorno ||
          data?.mensagemRetorno ||
          data?.MensagemRetorno ||
          data?.dsMensagem ||
          data?.msg ||
          data?.detail ||
          data?.Erro ||
          data?.error;
        let interceptorMsg: string | string[] = "";

        const status = e.response?.status;
        const customMsg = status
          ? customApiOptionsMemoized.errorMessages?.[status] || ""
          : "";

        switch (e.response?.status) {
          case 400: {
            if (Array.isArray(data)) {
              interceptorMsg = data;
            }
            break;
          }
          case 401: {
            if (!isLoggedIn) break;
            if (retrying) {
              onExpireSession();
              return { handled: true };
            }
            const hasRefreshedToken = await refreshAccessToken();
            return hasRefreshedToken ? { retry: true } : { handled: true };
          }
          case 403:
            interceptorMsg =
              "Erro desconhecido. Favor entrar em contato com o administrador do sistema.";
            navigate("/");
            break;
          case 425: {
            setLoading(true);

            if (retryCount < MAX_RETRIES) {
              retryCount++;
              const delay = Math.min(1000 * 2 ** retryCount, 30000);
              await new Promise((resolve) => setTimeout(resolve, delay));
              return { retry: true };
            }

            retryCount = 0;
            setLoading(false);
            interceptorMsg =
              "Tempo limite excedido. Tente novamente mais tarde.";
            break;
          }
          case 500:
            interceptorMsg = "Entre em contato com o administrador do sistema.";
            break;
          default:
            interceptorMsg =
              customApiOptionsMemoized.defaultErrorMessage ||
              "Ocorreu um erro inesperado. Entre em contato com o administrador do sistema.";
            break;
        }

        const errorMsg = apiMessage || customMsg || interceptorMsg;

        const { shouldSkipErrorSnackbar } =
          e.config as ICustomRequestConfig<unknown>;

        const showErrorSnackbar = (msg: string) => {
          if (shouldSkipErrorSnackbar?.(msg)) return;
          void message.error(msg);
        };

        if (errorMsg) {
          if (Array.isArray(errorMsg)) {
            errorMsg.forEach((msg) =>
              showErrorSnackbar(isString(msg) ? msg : msg.Erro),
            );
          } else if (
            typeof errorMsg !== "string" &&
            "errors" in errorMsg &&
            Array.isArray(errorMsg.errors)
          ) {
            errorMsg.errors.forEach((error: { errorMessage: string }) =>
              showErrorSnackbar(error.errorMessage),
            );
          } else if (typeof errorMsg === "object") {
            Object.values(errorMsg)
              .filter(
                (error) =>
                  typeof error === "string" && Number.isNaN(Number(error)),
              )
              .forEach((msg: string) => {
                showErrorSnackbar(msg);
              });
          } else {
            const err =
              typeof errorMsg === "string"
                ? errorMsg
                : "Erro desconhecido, entre em contato com o suporte.";
            showErrorSnackbar(err);
          }
        }

        return { handled: !!errorMsg };
      }

      return { handled: false };
    },
    [
      navigate,
      refreshAccessToken,
      onExpireSession,
      isLoggedIn,
      customApiOptionsMemoized,
      message,
    ],
  );

  const doRequest = useCallback(
    async (
      config?: AxiosRequestConfig<D>,
      retrying = false,
    ): Promise<AxiosResponse<T, unknown>> => {
      setLoading(true);

      await ensureAccessToken();

      if (controllerRef.current && isEqual(config, requestConfigRef.current)) {
        controllerRef.current.abort();
      }

      const controller = new AbortController();
      controllerRef.current = controller;

      requestConfigRef.current = config;

      try {
        const response = await apiInstance.request<T, AxiosResponse<T>, D>({
          ...baseConfigMemoized,
          ...config,
          signal: controller.signal,
        });

        retryCount = 0;
        setData(response.data);
        setError(null);
        setLoading(false);
        setIsFetched(true);

        return response;
      } catch (e: unknown) {
        if (!axios.isCancel(e)) {
          setError(e as E);
          setData(null);
          setLoading(false);
          setIsFetched(true);
        }

        const { retry, handled } = await handleError(e, retrying);
        if (retry) {
          return doRequest(config, retry);
        } else {
          setLoading(false);
          throw new ApiInterceptorError(!!handled, e);
        }
      }
    },
    [apiInstance, baseConfigMemoized, handleError, ensureAccessToken],
  );

  return [doRequest, { data, error, loading, isFetched, reset, cancel }];
}
